import { authenticatedFetch, getCsrfToken } from './authApi';
import { getEnv } from '../utils/env';

export type ErasureRecord = { source: string; club_id?: string | null; kind: string; action: string; count: number };
export type ErasureClub = { id: string; name: string; eligible: boolean; database_absent?: boolean; other_users: number; content: { kind: string; count: number }[] };
export type ErasureReview = {
  account?: { id: string; email: string; display_name: string | null };
  clubs?: ErasureClub[]; selected_clubs?: string[]; deleted_clubs?: number;
  records: ErasureRecord[]; blocked?: { kind: string }[]; revision?: string; retention_notice: string;
};
export type ErasureReceipt = { status: string; review: ErasureReview; removed_at?: string };
export class AccountErasureError extends Error {
  constructor(public readonly status: number, message: string, public readonly code?: string) { super(message); }
}
const base = () => getEnv('VITE_AI_API_URL', '').replace(/\/$/, '');
async function request<T>(path: string, body: unknown, authenticated = true): Promise<T> {
  const init: RequestInit = { method: 'POST', cache: 'no-store', headers: { 'Content-Type': 'application/json', 'X-API-Key': getEnv('VITE_AI_API_KEY', '') }, body: JSON.stringify(body) };
  const recoveryHeaders = new Headers(init.headers);
  const csrf = getCsrfToken();
  if (csrf) recoveryHeaders.set('X-CSRF-Token', csrf);
  // Recovery uses only the freshly authorized scoped capability, never token refresh.
  const response = authenticated
    ? await authenticatedFetch(`${base()}/api/v1/account/${path}`, init)
    : await fetch(`${base()}/api/v1/account/${path}`, { ...init, credentials: 'include', headers: recoveryHeaders });
  const payload = await response.json();
  if (!response.ok) throw new AccountErasureError(response.status,
    String(payload?.detail?.message || payload?.detail || payload?.error?.message || 'Account removal unavailable'),
    payload?.detail?.code);
  return payload as T;
}
export const getErasureReview = (clubs: string[] = []) => request<ErasureReview>('deletion-preview', { clubs });
export const confirmErasure = (password: string, revision: string, clubs: Record<string, string>) => request<{ token: string }>('deletion-confirmation', { password, revision, clubs });
export const executeErasure = (token: string) => request<ErasureReceipt>('delete', { token }, false);
export const getErasureStatus = (token: string) => request<ErasureReceipt>('deletion-status', { token }, false);
export async function getOwnAccountPdf(userId: string): Promise<Blob> {
  const response = await authenticatedFetch(`${base()}/api/v1/account/personal-data.pdf`, { cache: 'no-store' });
  if (!response.ok) throw new AccountErasureError(response.status, 'PDF unavailable');
  if (response.headers.get('X-Account-Id') !== userId || response.headers.get('Content-Type')?.split(';')[0] !== 'application/pdf') throw new Error('Invalid PDF response');
  const pdf = await response.blob();
  if (await pdf.slice(0, 5).text() !== '%PDF-') throw new Error('Invalid PDF file');
  return pdf;
}
